<template>
  <el-dialog
    v-model="dialogVisible"
    :title="dialogTitle"
    :fullscreen="fullscreen"
    width="92%"
    top="3vh"
    destroy-on-close
    append-to-body
    :class="{ 'cc-wfg--dark': isDarkTheme }"
  >
    <template #header>
      <div class="cc-wfg__header">
        <span class="cc-wfg__title">{{ dialogTitle }}</span>
        <el-button
          :icon="FullScreen"
          link
          size="small"
          @click="fullscreen = !fullscreen"
        />
      </div>
    </template>

    <div class="cc-wfg__host" v-loading="loading">
      <div v-if="!loading && isEmpty" class="cc-wfg__empty">
        {{ translate('No workflow defined yet.') }}
      </div>
      <workflow-graph
        v-else-if="effectiveRoot && !loading"
        :nodes="graphData.nodes"
        :edges="graphData.edges"
        @node-click="onNodeClick"
      />
    </div>

    <template #footer>
      <div class="cc-wfg__footer">
        <legend-bar />
        <el-button @click="dialogVisible = false">{{ translate('Close') }}</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { defineAsyncComponent, markRaw } from 'vue'
import { FullScreen } from '@element-plus/icons-vue'
import LegendBar from './legend-bar.vue'
import { buildGraph } from './composables/useWorkflowBuilder'

// markRaw so Vue doesn't try to wrap the icon component in a reactive proxy.
const FullScreenIcon = markRaw(FullScreen)

export default {
  name: 'CcWorkflowGraph',
  components: {
    LegendBar,
    // Vue 3 async-component wrapper — keeps the @vue-flow chunks out of the
    // main bundle while still resolving to a real component (a bare
    // `() => import()` would render as "[object Promise]").
    WorkflowGraph: defineAsyncComponent(() => import('./graph.vue')),
  },
  props: {
    visible:    { type: Boolean, default: false },
    /** Root entity: a Component template OR a Position/Order item with actions + children. */
    root:       { type: Object, default: null },
    /** 'component' | 'position' | 'order' | 'inquiry' — affects title and status sourcing. */
    entityType: { type: String, default: 'component' },
  },
  emits: ['update:visible', 'node-click'],
  data() {
    return {
      FullScreen: FullScreenIcon,
      fullscreen: false,
      loading: false,
      hydratedRoot: null,
      // Session-level cache of fetched Component DTOs, keyed by id. Survives
      // dialog close/open so re-opening the same workflow is instant.
      componentCache: new Map(),
    }
  },
  watch: {
    visible(v) {
      if (v) this.hydrate()
      else this.hydratedRoot = null
    },
  },
  computed: {
    dialogVisible: {
      get() { return this.visible },
      set(v) { this.$emit('update:visible', v) },
    },
    dialogTitle() {
      const r = this.root
      const name = r?.name || r?.productName || r?.text
        || r?.component?.name || r?.component?.productName || r?.component?.text
        || ''
      const kind = this.translate(
        this.entityType === 'position' ? 'Position workflow' :
        this.entityType === 'order'    ? 'Order workflow'    :
        this.entityType === 'inquiry'  ? 'Position workflow' :
                                         'Component workflow'
      )
      return name ? `${kind} — ${name}` : kind
    },
    statusByActionId() {
      const map = new Map()
      if (!this.showStatus) return map
      collectActionStatuses(this.effectiveRoot, map)
      return map
    },
    effectiveRoot() {
      // Use the hydrated root once children have been fetched. Fall back to the
      // raw root when no hydration is needed (e.g. orders, where children already
      // carry their actions).
      return this.hydratedRoot || this.root
    },
    showStatus() {
      return this.entityType === 'order' || this.entityType === 'position'
    },
    useQuantity() {
      // Orders, positions, and inquiries all carry a top-level Quantity that
      // multiplies the per-piece duration.
      return this.entityType === 'order'
        || this.entityType === 'position'
        || this.entityType === 'inquiry'
    },
    graphData() {
      if (!this.effectiveRoot) return { nodes: [], edges: [] }
      return buildGraph(this.effectiveRoot, {
        statusByActionId: this.statusByActionId,
        showStatus: this.showStatus,
        useQuantity: this.useQuantity,
        globalMetadata: this.globalMetadata,
      })
    },
    isEmpty() {
      const r = this.effectiveRoot
      if (!r) return false
      return !hasAnyActions(r)
    },
    isDarkTheme() {
      return this.globalMetadata
        && this.globalMetadata.appSkin
        && this.globalMetadata.appSkin.name === 'tech-dark'
    },
  },
  methods: {
    onNodeClick(node) { this.$emit('node-click', node) },

    // ---- Sub-component hydration ----
    // The `/v1/components/{id}` payload returns sub-children as id+name+quantity
    // stubs only — no `actions` array. To visualize sub-component workflows we
    // recursively fetch each child via the same existing endpoint when the
    // popup opens, caching by id so re-opens are instant.
    async hydrate() {
      if (!this.root) {
        this.hydratedRoot = null
        return
      }
      const container = getChildrenContainer(this.root)
      const hasChildren = container && Array.isArray(container.children) && container.children.length > 0
      // Skip the network round-trip if there's nothing to hydrate or children
      // already carry actions (the order case).
      const needsHydration = hasChildren && container.children.some(c => !Array.isArray(c.actions))
      if (!needsHydration) {
        this.hydratedRoot = this.root
        return
      }
      this.loading = true
      try {
        const cloned = safeDeepClone(this.root)
        await this.hydrateChildrenRecursively(cloned, 0)
        this.hydratedRoot = cloned
      } catch (e) {
        // Fall back to the un-hydrated root so at least the main flow renders.
        // eslint-disable-next-line no-console
        console.error('[workflowgraph] hydration error', e)
        this.hydratedRoot = this.root
      } finally {
        this.loading = false
      }
    },

    async hydrateChildrenRecursively(node, depth) {
      if (depth > 6) return  // matches builder's depth cap
      const container = getChildrenContainer(node)
      if (!container) return
      const children = container.children || []
      if (!children.length) return

      const tasks = children.map(async c => {
        // Already carries actions? (order case) — keep as-is.
        if (Array.isArray(c.actions)) return c
        if (!c.id) return c
        const fetched = await this.fetchComponent(c.id)
        if (!fetched) return c
        // Merge: keep child-stub fields that the parent payload carries
        // (idx/quantity), take everything else from the full Component.
        return {
          ...fetched,
          idx: c.idx ?? fetched.idx,
          quantity: c.quantity ?? fetched.quantity,
        }
      })
      const hydrated = await Promise.all(tasks)
      container.children = hydrated

      // Recurse into each newly-hydrated child.
      await Promise.all(hydrated.map(c => this.hydrateChildrenRecursively(c, depth + 1)))
    },

    async fetchComponent(id) {
      if (this.componentCache.has(id)) return this.componentCache.get(id)
      try {
        const res = await this.$http.get(`v1/components/${id}`)
        const comp = res && res.data && res.data.component
        if (comp) this.componentCache.set(id, comp)
        return comp || null
      } catch (e) {
        // Skip this subtree on failure rather than abort the whole hydration.
        return null
      }
    },
  },
}

function getChildrenContainer(node) {
  if (!node) return null
  if (Array.isArray(node.children)) return node
  if (node.component && Array.isArray(node.component.children)) return node.component
  return null
}

function safeDeepClone(node) {
  // Strip `parent` back-refs that Vue may have wired up (would cause cycles in JSON).
  return JSON.parse(JSON.stringify(node, (k, v) => (k === 'parent' ? undefined : v)))
}

function collectActionStatuses(node, map) {
  if (!node) return
  ;(node.actions || []).forEach(a => map.set(a.id, a.status))
  ;(node.children || []).forEach(c => collectActionStatuses(c, map))
}

function hasAnyActions(node) {
  if (!node) return false
  if ((node.actions || []).length > 0) return true
  return (node.children || []).some(c => hasAnyActions(c))
}
</script>

<style lang="scss" scoped>
.cc-wfg {
  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    
  }
  &__title {
    flex: 1;
    font-weight: 600;
    
  }
  &__host {
    height: 75vh;
    min-height: 520px;
    background: #fafafa;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    position: relative;
  }
  &__empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 13px;
  }
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
}
</style>

<style lang="scss">
/* Dark theme overrides — unscoped because <el-dialog append-to-body> teleports
   the workflow graph outside this component's scoped DOM tree. */
.cc-wfg--dark {
  $node-bg:     #2A2D31;
  $node-text:   #D3D4C9;
  $node-muted:  #8E8E8E;
  $node-strong: #DFE6EC;
  $node-border: #4A4A4A;

  .cc-wfg__host {
    background: var(--background-color, #1B1B1B);
    border-color: $node-border;
  }
  .cc-wfg__empty { color: $node-muted; }

  /* Node card body text is handled inside each node template via theme
     CSS variables (--main-text-color, --label-color, etc.) — no overrides
     needed here. Scoped styles inside the node templates would beat any
     unscoped rule we put here on specificity tie anyway. */

  /* Subcomponent group container: keep a visible tint on dark canvas. */
  .cc-group {
    background: rgba(64, 158, 255, 0.10);
    border-color: #5BA1E6;
  }
  .cc-group__badge {
    background: $node-bg;
    color: #5BA1E6;
    border-color: #5BA1E6;
  }

  /* VueFlow canvas, edges, controls, minimap. */
  .vue-flow {
    background: var(--background-color, #1B1B1B);
  }
  .vue-flow__background {
    background: var(--background-color, #1B1B1B);
  }
  .vue-flow__edge-path {
    stroke: #888;
  }
  .vue-flow__controls {
    background: $node-bg;
    border: 1px solid $node-border;
    box-shadow: 0 1px 4px rgba(0, 0, 0, .4);
  }
  .vue-flow__controls-button {
    background: $node-bg;
    border-color: $node-border;
    color: $node-text;
    fill: $node-text;
    svg { fill: $node-text; }
    &:hover {
      background: var(--alt-background-color, #3C3C40);
    }
  }
  /* Legend at the footer. */
  .cc-legend             { color: $node-text; }
  .cc-legend__label      { color: $node-strong; }
  .cc-legend__cat {
    border-color: $node-border;
    background: $node-bg;
  }
  .cc-legend__cat--dashed { color: $node-muted; }
  .cc-legend__chip--muted { color: #303133; } /* on light grey bg, keep dark */
}
</style>
